import {
	Component,
	ComponentInterface,
	Element,
	Event,
	EventEmitter,
	Listen,
	Prop,
	Watch,
} from '@stencil/core';
import { dsConsole } from '../_helpers/console';
import { CardAppearance } from '../../../../_storybook-helpers/cards';
import { RowSizes } from './hero-number-card.interface';
import { IsharesStandardBackgroundColor } from '../../../../types/colors';
import isChromatic from 'chromatic/isChromatic';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

const MinFontSize = 20; // The minimum allowed font size that can be set automatically
const MaxFontSize = 120; // The maximum allowed font size that can be set automatically
const NumericElementsAcceptableRatioMin = 0.9; // The rough minimum amount of space that the prefix+number+suffix should occupy within the row
const NumericElementsAcceptableRatioMax = 1; // The rough maxium amount of space that the prefix+number+suffix should occupy within the row
const PrefixSuffixFontRatio = 0.45; // The exact ratio of the prefix/suffix font size, compared to the number field's font size
const WindowResizeDebounceDelay = 200; // Debounce delay in milliseconds to prevent window resize being fired too many times

@Component({
	tag: 'ds-hero-number-card',
})
export class HeroNumberCard implements ComponentInterface {
	@Element() el: HTMLDsHeroNumberCardElement;

	numberRowEl: HTMLElement;
	numberEl: HTMLElement;
	prefixEl: HTMLElement;
	suffixEl: HTMLElement;

	private setFontSizeTimer: number;

	@Prop({ mutable: true }) appearance: CardAppearance = 'standard';
	@Prop({ mutable: true }) bgColor: IsharesStandardBackgroundColor = 'white';
	@Prop({ mutable: true }) autoFontSize = false; // Calculate the font size based on the space available instead of relying on viewport width

	@Prop() prefixValue: string; // 'prefix' is a 'reserved public name' causing a build error so we have to use this prop name instead
	@Prop() suffixValue: string;
	// updated sizes of number-row
	@Prop() rowSizes: RowSizes;

	@Event() fontSizeChange: EventEmitter;

	@Watch('rowSizes')
	omRowSizeChange() {
		this.fontSizeChange.emit();
	}

	@Watch('prefixValue')
	onPrefixChange(newValue: string) {
		this.renderPrefixOrSuffix('prefix', newValue);
	}

	@Watch('suffixValue')
	onSuffixChange(newValue: string) {
		this.renderPrefixOrSuffix('suffix', newValue);
	}

	renderPrefixOrSuffix(prefixOrSuffix: 'prefix' | 'suffix', newValue) {
		if (newValue && newValue.length) {
			if (newValue.length > 1) {
				this[
					prefixOrSuffix + 'El'
				].innerHTML = `<i class="fa fa-${newValue}"></i>`;
			} else {
				this[
					prefixOrSuffix + 'El'
				].innerHTML = `<span class="ds-text-as-icon">${newValue}</span>`;
			}
		} else {
			this[prefixOrSuffix + 'El'].innerHTML = '';
		}
		this.triggerFontSizeUpdateIfNeeded();
	}

	componentWillLoad(): void {
		this.numberRowEl = this.el.querySelector('.ds-number-row');
		this.numberEl = this.el.querySelector('.ds-number');
		this.prefixEl = this.el.querySelector('.ds-prefix');
		this.suffixEl = this.el.querySelector('.ds-suffix');

		if (this.autoFontSize) {
			this.triggerFontSizeUpdateIfNeeded();
		}
	}

	componentDidLoad() {
		this.renderPrefixOrSuffix('prefix', this.prefixValue);
		this.renderPrefixOrSuffix('suffix', this.suffixValue);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	/**
	 * Listen to the window being resized and after a 'debounce' period, adjust the font size.
	 */
	@Listen('resize', { target: 'window' })
	async windowResizeHandler(): Promise<void> {
		if (!this.autoFontSize) {
			return;
		}
		if (this.setFontSizeTimer) {
			window.clearTimeout(this.setFontSizeTimer);
		}
		this.setFontSizeTimer = window.setTimeout(
			() => this.triggerFontSizeUpdateIfNeeded(),
			WindowResizeDebounceDelay
		);
	}

	/**
	 * Calculate the current ratio of 'numeric elements' compared to the row they are in
	 * The resulting ratio will be > 1 if the 'numeric elements' occupy more space than the row, ie. they are cut off at the edges
	 */
	calculateCurrentRatio() {
		const numberRowWidth = this.numberRowEl ? this.numberRowEl.offsetWidth : 0;
		const numberElWidth = this.numberEl ? this.numberEl.offsetWidth : 0;
		const prefixElWidth = this.prefixEl ? this.prefixEl.offsetWidth : 0;
		const suffixElWidth = this.suffixEl ? this.suffixEl.offsetWidth : 0;
		const numericElementsWidth = prefixElWidth + numberElWidth + suffixElWidth;

		const ratio = numericElementsWidth / numberRowWidth;

		return ratio;
	}

	/**
	 * Adjust font size to fit space available, if the 'numeric elements' are not already occupying an acceptable amount
	 * of space.
	 */
	triggerFontSizeUpdateIfNeeded() {
		const currentRatio = this.calculateCurrentRatio();

		// If numeric elements are occupying less space than they should be or more space than they should be
		if (
			!isChromatic() &&
			(currentRatio < NumericElementsAcceptableRatioMin ||
				currentRatio > NumericElementsAcceptableRatioMax)
		) {
			const targetRatio = 1 / currentRatio;
			this.updateFonts(targetRatio);
		}
	}

	/**
	 * Update the font sizes and line heights of the number field, prefix field, and suffix field, to fit the space available.
	 *
	 * @param ratio number  The ratio to increase or decrease the font size. Above one to increase, below one - eg. 0.9 - to decrease.
	 */
	updateFonts(ratio: number) {
		const numberElCompStyles = window.getComputedStyle(this.numberEl);
		const numberFontSize = parseInt(
			numberElCompStyles.fontSize.replace('px', '')
		);
		let newFontSize;

		if (ratio === 1 || isNaN(ratio)) {
			dsConsole.error(
				`Hero number card is attempting to update fonts based on an invalid ratio	of "${ratio}"`
			);
		}
		if (ratio > 1) {
			newFontSize = numberFontSize * ratio;
			newFontSize = newFontSize > MaxFontSize ? MaxFontSize : newFontSize;
			this.numberEl.style.fontSize = newFontSize + 'px';
			this.numberEl.style.lineHeight = newFontSize + 'px';
		}

		if (ratio < 1) {
			newFontSize = numberFontSize * ratio;
			newFontSize = newFontSize < MinFontSize ? MinFontSize : newFontSize;
			this.numberEl.style.fontSize = newFontSize + 'px';
			this.numberEl.style.lineHeight = newFontSize + 'px';
		}

		const prefixSuffixNewFontSize = newFontSize * PrefixSuffixFontRatio;

		if (this.prefixEl) {
			this.prefixEl.style.fontSize = prefixSuffixNewFontSize + 'px';
			this.prefixEl.style.lineHeight = prefixSuffixNewFontSize + 'px';
		}

		if (this.suffixEl) {
			this.suffixEl.style.fontSize = prefixSuffixNewFontSize + 'px';
			this.suffixEl.style.lineHeight = prefixSuffixNewFontSize + 'px';
		}
		this.rowSizes = {
			prefixFontSize: this.prefixEl.style.fontSize,
			numberFontSize: this.numberEl.style.fontSize,
			suffixFontSize: this.suffixEl.style.fontSize,
		};
		moveInlineStylesToHead(this.el.id);
	}
}
